/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
} from '@gh/builder-shared';
import { AelEvents, BuilderPermission, EMPTY_XERO_SYNC_PROGRESS, SystemRoleBit, UserType } from '@gh/builder-shared-data';
import {
  getRouteInitData,
  getRouteQueryParam,
  pollProgress,
  ProgressStatusEventType,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { Confirmation, DialogService, NotificationService, WaitingService, yesNoConfirmation } from '@gh/core-ui';
import { openInThisWindow } from '@gh/core-util';
import { filter$, switchMap$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import { Subscription } from 'rxjs';
import {
  isXeroSyncInProgress,
  SystemVarsAccountsXeroSync,
  SystemVarsAccountsXeroUrl,
} from '../../../resources/model/system-vars-accounts.model';
import { SystemVarsAccountsTabBusinessService } from '../../../resources/system-vars-accounts-tab-business.service';
import { SystemVarsAccountsXeroFormState } from './system-vars-accounts-xero-form.service';

@Component({
  standalone: false,
  selector: 'gh-system-vars-accounts-xero-subtab',
  templateUrl: 'system-vars-accounts-xero-subtab.component.html',
  styleUrls: ['system-vars-accounts-xero-subtab.component.scss'],
  viewProviders: [
    SystemVarsAccountsXeroFormState,
  ],
})
export class SystemVarsAccountsXeroTabComponent implements OnInit, OnDestroy {
  @ViewChild('xeroWebhookUrl') xeroWebhookUrlEl: ElementRef;
  @ViewChild('xeroRedirectUri', { static: true }) xeroRedirectUriEl: ElementRef;
  get isConsolidatedApp(): boolean {
    return this.originalXero?.xeroAppModel === 'CONSOLIDATED_XERO_APP';
  }
  get isCorporateOrMasterUser(): boolean {
    return Boolean(
      this.userContext?.userType === UserType.Corporate
      || this.userContext?.userType === UserType.Master
      || this.userContext?.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext?.hasSystemRole(SystemRoleBit.Master)
      || this.userContext?.corporateView
    );
  }
  get isCorporateUser(): boolean {
    return this.isCorporateOrMasterUser;
  }
  get canMigrate(): boolean {
    const isCorporate = this.isCorporateUser;
    const hasEditPermission = Boolean(
      this.userContext?.hasPermission(BuilderPermission.Setup.SystemVariables.Accounts.EDIT)
    );
    return !this.isConsolidatedApp && (isCorporate || hasEditPermission);
  }
  get hasModifyPermission(): boolean {
    if (this.isCorporateOrMasterUser) {
      return true;
    }
    if (this.isXeroConnected) {
      return this.isModifyMode;
    }
    return Boolean(this.form?.editable);
  }
  get isMigrateDisabled(): boolean {
    return Boolean(this.form?.isAnySyncInProgress) || !this.hasModifyPermission;
  }
  isBannerDismissed: boolean = false;
  isXeroConnected: boolean = false;
  isModifyMode: boolean = false;
  saveWebhooKeyDisabled: boolean = false;
  originalXero: any;
  xeroSyncOptions: SystemVarsAccountsXeroSync = {
    syncCostCenters: false,
    syncJobNumber: false,
    syncContacts: false,
    syncInvoices: false,
  };

  dismissBanner(): void {
    this.isBannerDismissed = true;
  }

  private progressSubscription: Subscription;

  constructor(
    public form: SystemVarsAccountsXeroFormState,
    private route: ActivatedRoute,
    private router: Router,
    private systemVarsBusiness: SystemVarsAccountsTabBusinessService,
    private navigationService: BuilderNavigationService,
    private notifications: NotificationService,
    private dialogs: DialogService,
    private pipelines: FormActionPipelines,
    private actions: CommonActions,
    private userContext: BuilderUserContext,
    private waitingService: WaitingService,
  ) {
  }

  ngOnInit(): void {
    const { originalXero } = getRouteInitData(this.route);
    const code = getRouteQueryParam(this.route, 'code');
    const state = getRouteQueryParam(this.route, 'state');
    const oauthError = getRouteQueryParam(this.route, 'error');
    const officeId = this.userContext.office?.id;

    if (!code && oauthError) {
      // Xero redirected back without an authorization code (user cancelled or Xero refused). Nothing was changed on
      // the server, so only clean up the URL and tell the user.
      this.removeParamFromUrl();
      this.notifications.warning('Xero authorization was cancelled or failed. The Xero connection was not changed.');
    }

    let pendingConfig: any = null;
    if (code) {
      pendingConfig = this.getPendingConfig(officeId);
    } else {
      this.clearPendingConfig(officeId);
    }

    const hasPendingConnection = Boolean(code || pendingConfig);

    if (code) {
      const organizationName = pendingConfig?.organizationName
        || (officeId ? sessionStorage.getItem(`pending_xero_org_${officeId}`) : null)
        || originalXero?.organizationName
        || '';

      this.systemVarsBusiness.connectXero(code, organizationName, state).pipe(
        this.waitingService.operator(),
        switchMap$((data) =>  [data]))
        .subscribe(
          (data) => {
            if (data) {
              this.isXeroConnected = true;
              this.originalXero = data;
              this.form.build(data);
              this.form.setFieldsPrivilegedEnabled(false);
              this.syncReactiveFormDisabledState();
              this.notifyXeroReconsentIfNeeded(data);
              this.clearPendingConfig(officeId);
            } else {
              this.form.clearConnectionFields();
              this.clearPendingConfig(officeId);
            }
            this.removeParamFromUrl();
          },
          (error) => {
            this.clearPendingConfig(officeId);
            this.removeParamFromUrl();
            this.notifications.processAll(error);
          });
    }

    if (pendingConfig && originalXero) {
      if (pendingConfig.organizationName) {
        originalXero.organizationName = pendingConfig.organizationName;
      }
      if (pendingConfig.selectedFinancialYear) {
        originalXero.selectedFinancialYear = pendingConfig.selectedFinancialYear;
      }
      originalXero.preserveCredentials = true;
    }

    assign(this, { originalXero });
    this.form.build(originalXero);
    this.setIsConnected(hasPendingConnection);
    this.updateSaveWebhookDisabled();
    this.syncReactiveFormDisabledState();

    if (pendingConfig) {
      this.form.group.patchValue({
        organizationName: pendingConfig.organizationName || '',
        selectedFinancialYear: pendingConfig.selectedFinancialYear || '',
      });
    }

    if (!code) {
      this.notifyXeroReconsentIfNeeded(originalXero);
    }

    if (this.form.isAnySyncInProgress) {
      this.notifications.warning('msg_sync_in_progress');
    }

    this.progressSubscription = pollProgress({
      empty: EMPTY_XERO_SYNC_PROGRESS,
      isInProgress: isXeroSyncInProgress,
      status: this.form.progress$,
      poll: () => this.systemVarsBusiness.getXeroSyncProgress(),
    }).subscribe(({ type, nextStatus }) => {
      if (type !== ProgressStatusEventType.Start || this.form.isInitialSyncSuccessed) {
        this.form.setSyncProgress(nextStatus);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  get isUserSwitched(): boolean {
    return this.userContext.isSwitched();
  }

  get isModifyDisabled(): boolean {
    return !this.isXeroConnected || this.isModifyMode;
  }

  get isEditOrModifyMode(): boolean {
    return this.form.editable || this.isModifyMode;
  }

  get isWebhookKeyActive(): boolean {
    return Boolean(this.form.getControl('xeroWebhookKey').value);
  }

  get isWebhookUrlActive(): boolean {
    return Boolean(this.form.getControl('xeroWebhookUrl').value);
  }

  get isRedirectUriActive(): boolean {
    return Boolean(this.form.getControl('xeroCallbackUrl').value);
  }

  get isFormFieldHidden(): boolean {
    return this.isXeroConnected && !this.isModifyMode;
  }

  get saveWebhookDisabled(): boolean {
    return !this.isEditOrModifyMode || !this.isWebhookUrlActive
      || !this.isWebhookKeyActive || this.saveWebhooKeyDisabled;
  }

  private getPendingConfig(officeId?: ID | number): any {
    if (!officeId) {
      return null;
    }
    const raw = sessionStorage.getItem(`pending_xero_config_${officeId}`);
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  private savePendingConfig(officeId?: ID | number): void {
    if (!officeId) {
      return;
    }
    const pendingConfig = {
      organizationName: this.form.getControl('organizationName')?.value || this.originalXero?.organizationName || '',
      selectedFinancialYear: this.form.getControl('selectedFinancialYear')?.value || '',
    };
    sessionStorage.setItem(`pending_xero_config_${officeId}`, JSON.stringify(pendingConfig));
    if (pendingConfig.organizationName) {
      sessionStorage.setItem(`pending_xero_org_${officeId}`, pendingConfig.organizationName);
    }
  }

  private clearPendingConfig(officeId?: ID | number): void {
    if (!officeId) {
      return;
    }
    sessionStorage.removeItem(`pending_xero_config_${officeId}`);
    sessionStorage.removeItem(`pending_xero_org_${officeId}`);
  }

  updateSaveWebhookDisabled(): void {
    const webhookKeyValue = this.form.getControl('xeroWebhookKey').value;
    this.saveWebhooKeyDisabled = webhookKeyValue === this.originalXero.xeroWebhookKey;
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      eventTypeIds: [AelEvents.Office.IntegrationAccounts],
      label: label ? label : 'lbl_integration_accounts',
    });
  }

  setIsConnected(hasPendingConnection: boolean = false): void {
    this.isXeroConnected = Boolean(
      this.originalXero && (
        this.originalXero.tenantId ||
        (!this.isConsolidatedApp && this.originalXero.organizationName && !hasPendingConnection && !this.originalXero.preserveCredentials)
      )
    );

    if (this.isXeroConnected) {
      this.form.setFieldsPrivilegedEnabled(false);
    } else if (!hasPendingConnection && !this.originalXero?.preserveCredentials && !this.isConsolidatedApp) {
      this.form.getControl('xeroClientId')?.setValue('');
      this.form.getControl('xeroClientSecret')?.setValue('');
      if (this.originalXero) {
        this.originalXero.xeroClientId = '';
        this.originalXero.xeroClientSecret = '';
      }
    }
  }

  setSyncOption(event: MatCheckboxChange): void {
    this.xeroSyncOptions[event.source.value] = event.checked;
  }

  connect(): void {
    const officeId = this.userContext.office?.id;
    this.savePendingConfig(officeId);
    const etag = this.form.getControl('etag')?.value || this.originalXero?.etag;
    if (etag && !this.form.getControl('etag')?.value) {
      this.form.getControl('etag')?.setValue(etag);
    }
    const payload = this.buildXeroAuthPayload(etag);
    this.systemVarsBusiness
      .generateXeroAcceptUrl(payload)
      .pipe(this.waitingService.operator())
      .subscribe(
        (xeroAcceptUrl: SystemVarsAccountsXeroUrl) =>
        openInThisWindow(xeroAcceptUrl.url),
        (error: any) => {
          this.clearPendingConfig(officeId);
          this.notifications.processAll(error);
        });
  }

  modify(): void {
    const officeId = this.userContext.office?.id;
    this.savePendingConfig(officeId);
    const etag = this.form.getControl('etag')?.value || this.originalXero?.etag;
    if (etag && !this.form.getControl('etag')?.value) {
      this.form.getControl('etag')?.setValue(etag);
    }
    const payload = this.buildXeroAuthPayload(etag);
    this.systemVarsBusiness
      .modifyXero(payload)
      .pipe(this.waitingService.operator())
      .subscribe(
        (xeroAcceptUrl: SystemVarsAccountsXeroUrl) =>
        openInThisWindow(xeroAcceptUrl.url),
        (error: any) => {
          this.clearPendingConfig(officeId);
          this.notifications.processAll(error);
        });
  }

  generateWebhookUrl(): void {
    this.systemVarsBusiness
      .getXeroWebhookUrl().pipe(
      (this.waitingService.operator()))
      .subscribe(
        (data) => {
          this.originalXero = data;
          this.form.build(data);
          if (this.isXeroConnected) {
            this.form.setFieldsPrivilegedEnabled(false);
          }
          this.syncReactiveFormDisabledState();
          this.notifyXeroReconsentIfNeeded(data);
        },
        this.notifications.processAll,
      );
  }

  copyUrl(type: string): void {
    this[type].nativeElement.select();
    document.execCommand('copy');
  }

  saveWebhookKey(): void {
    this.systemVarsBusiness
      .saveXeroWebhookKey({
                            webhookKey: this.form.getControl('xeroWebhookKey').value,
                            etag: this.form.getControl('etag').value,
      }).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (data) => {
          this.originalXero = data;
          this.form.build(data);

          if (this.isXeroConnected) {
            this.form.setFieldsPrivilegedEnabled(false);
          }

          this.syncReactiveFormDisabledState();
          this.saveWebhooKeyDisabled = true;
          this.notifyXeroReconsentIfNeeded(data);
        },
        this.notifications.processAll,
      );
  }

  sync(): void {
    this.systemVarsBusiness
      .syncWithXero(this.xeroSyncOptions).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (sync) => {
          this.form.setSyncProgress(sync.progress);
          this.notifications.success('msg_synchronization_initiated_successfully');
        },
        this.notifications.processAll,
      );
  }

  resync(): void {
    this.systemVarsBusiness.restartXeroSync()
      .pipe(this.waitingService.operator())
      .subscribe(
        ({ progress }) => this.form.setSyncProgress(progress),
        this.notifications.processAll);
  }

  onPrivilegedTokenReceived(): void {
    this.isModifyMode = true;
    this.form.setFieldsPrivilegedEnabled(true);
    this.syncReactiveFormDisabledState();
  }

  private syncReactiveFormDisabledState(): void {
    const financialYearCtrl = this.form.getControl('selectedFinancialYear');
    if (!this.originalXero?.financialYears?.length) {
      financialYearCtrl.disable({ emitEvent: false });
    }

    const webhookKeyCtrl = this.form.getControl('xeroWebhookKey');
    if (!this.isEditOrModifyMode) {
      webhookKeyCtrl.disable({ emitEvent: false });
    } else {
      webhookKeyCtrl.enable({ emitEvent: false });
    }

    const webhookUrlCtrl = this.form.getControl('xeroWebhookUrl');
    if (webhookUrlCtrl) {
      if (!this.isEditOrModifyMode) {
        webhookUrlCtrl.disable({ emitEvent: false });
      } else {
        webhookUrlCtrl.enable({ emitEvent: false });
      }
    }
  }

  removeParamFromUrl(): void {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: { code: null, state: null, error: null },
        queryParamsHandling: 'merge',
      });
  }

  private notifyXeroReconsentIfNeeded(settings: { requiresReconsent?: boolean } | null | undefined): void {
    if (settings?.requiresReconsent === false) {
      // If requiresReconsent is false - it means that we have just received new access token and we can notify user that if they want to avoid reconnecting Xero in the future, they should provide consent to receive privileged token
      // this.notifications.warning('msg_xero_reconnect_reconsent');
    }
  }

  /**
   * Payload for generate-accept-url / modify.
   */
  private buildXeroAuthPayload(etag: string): any {
    const payload: any = {
      ...this.form.submitValue,
      xeroAppModel: this.isConsolidatedApp ? 'CONSOLIDATED_XERO_APP' : 'OFFICE_XERO_APP',
      etag: etag || this.form.submitValue?.etag,
    };
    return payload;
  }

  migrateToConsolidatedApp(): void {
    if (this.isMigrateDisabled) {
      return;
    }
    const wasConnected = this.isXeroConnected;
    const messageId = wasConnected
      ? 'msg_xero_migrate_to_consolidated_app'
      : 'msg_xero_connect_to_consolidated_app';

    this.dialogs.confirmation(
      yesNoConfirmation(messageId)
    ).pipe(
      filter$((result) => result === Confirmation.Yes),
      switchMap$(() => this.systemVarsBusiness.migrateToConsolidatedApp({
        // Only the privileged (authorization number) token is needed by /migrate; credentials are never sent.
        privilegedTokens: this.form.submitValue?.privilegedTokens,
      }).pipe(
        this.waitingService.operator(),
      )),
    ).subscribe(
      (data) => {
        if (data) {
          const officeId = this.userContext.office?.id;
          this.clearPendingConfig(officeId);
          this.isBannerDismissed = false;
          this.originalXero = data;
          this.form.build(data);
          if (data.etag) {
            this.form.getControl('etag')?.setValue(data.etag);
          }
          this.setIsConnected();
          this.updateSaveWebhookDisabled();
          this.syncReactiveFormDisabledState();
          // The backend persists nothing until the consolidated OAuth round-trip succeeds.
          const successMessage = wasConnected
            ? 'Consolidated One Xero App selected. Connect to Xero to complete the migration.'
            : 'Consolidated One Xero App selected. Connect to Xero to complete the setup.';
          this.notifications.success(successMessage);
        }
      },
      (error) => this.notifications.processAll(error)
    );
  }

  connectToConsolidatedApp(): void {
    this.migrateToConsolidatedApp();
  }
}
